/**
 * ProvidersList Component
 *
 * Displays list of providers with their audit metrics
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PeopleOutline as EmptyIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useProvidersList } from '../../hooks/useProvidersList';

import { ClinicianTableHeader, ClinicianTableRow } from './ClinicianTableRow';
import { PracticeOverview } from './PracticeOverview';
import {
  ProvidersFilters,
  type ProvidersFilters as ProvidersFiltersType,
} from './ProvidersFilters';

interface ProvidersListProps {
  /** Organization ID for admin mode */
  organizationId?: string;
}

export const ProvidersList: React.FC<ProvidersListProps> = ({ organizationId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { jotauditDashboard } = useEventTracking();

  const [filters, setFilters] = useState<ProvidersFiltersType>({
    days: 30, // Default to 30 days
  });

  const previousFiltersRef = useRef<ProvidersFiltersType>(filters);
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  const { providers, isLoading, error, refetch, isEmpty } = useProvidersList({
    organizationId,
    days: filters.days,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });

  // Track when filters change to show skeletons
  useEffect(() => {
    const filtersChanged =
      previousFiltersRef.current.days !== filters.days ||
      previousFiltersRef.current.dateFrom !== filters.dateFrom ||
      previousFiltersRef.current.dateTo !== filters.dateTo;

    if (filtersChanged && isLoading) {
      setIsFilterChanging(true);
    } else if (!isLoading) {
      setIsFilterChanging(false);
    }

    previousFiltersRef.current = filters;
  }, [filters, isLoading]);

  const handleProviderClick = useCallback(
    (providerId: string, providerName?: string) => {
      // Track provider click
      jotauditDashboard.providerClicked({
        provider_id: providerId,
        provider_name: providerName,
      });
      // Navigate to encounters tab filtered by this provider
      if (organizationId) {
        navigate(`/admin-v2/jotaudit/${organizationId}?tab=encounters&provider=${providerId}`);
      } else {
        navigate(`/jotaudit?tab=encounters&provider=${providerId}`);
      }
    },
    [navigate, jotauditDashboard, organizationId],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading providers</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F8F9FB', p: 3, borderRadius: 4, mt: -3, mx: -3 }}>
      {/* Top Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ flex: 1 }} />
        <Tooltip
          title="Get in touch to add more clinicians to JotAudit. contact@jotpsych.com"
          arrow
        >
          <span>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              disabled
              sx={{
                bgcolor: theme.palette.grey[300],
                color: theme.palette.text.disabled,
                textTransform: 'uppercase',
                fontWeight: 600,
                borderRadius: '8px',
                px: 3,
                '&:hover': {
                  bgcolor: theme.palette.grey[300],
                },
              }}
            >
              Invite User
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Filters */}
      <ProvidersFilters filters={filters} onFiltersChange={setFilters} isLoading={isLoading} />

      {/* Practice Overview */}
      {isFilterChanging ? (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="circular" width={16} height={16} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
            <Box sx={{ gridColumn: 'span 2' }}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ gridColumn: 'span 2' }}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ gridColumn: 'span 4' }}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ gridColumn: 'span 4' }}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
            </Box>
          </Box>
        </Box>
      ) : (
        <PracticeOverview providers={providers} />
      )}

      {/* Clinicians Table */}
      <Box>
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <ClinicianTableHeader />

          {/* Loading State - Show skeletons when loading (initial load or filter change) */}
          {isLoading || isFilterChanging ? (
            <Box>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '48px 1.5fr 1fr 1.2fr 1fr 1fr 1.2fr 48px',
                    alignItems: 'center',
                    px: 2,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Skeleton variant="rectangular" width={24} height={24} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="60%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton width="40%" height={16} />
                  </Box>
                  <Skeleton width={40} height={20} />
                  <Skeleton width={80} height={24} />
                  <Skeleton width={100} height={20} />
                  <Skeleton width={60} height={20} />
                  <Skeleton width={60} height={20} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              ))}
            </Box>
          ) : isEmpty ? (
            /* Empty State */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: theme.palette.grey[100],
                  mb: 2,
                }}
              >
                <EmptyIcon sx={{ fontSize: 32, color: theme.palette.grey[400] }} />
              </Box>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                No providers found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
              >
                Audit data will appear here once notes are audited
              </Typography>
            </Box>
          ) : (
            /* Provider List */
            <Box>
              {providers.map(provider => (
                <ClinicianTableRow
                  key={provider.user_id}
                  provider={provider}
                  onClick={handleProviderClick}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ProvidersList;
